import { useEffect, useRef, useState } from "react"
import { LogIn, LogOut, Settings, ChevronDown } from "lucide-react"

export interface AccountMenuUser {
  email: string
  name: string | null
}

export interface AccountMenuProps {
  /** Base URL of the central auth service (e.g. https://auth.lilbillbiscuit.com). */
  authBase: string
  /** The signed-in user, or null when unauthenticated. */
  user: AccountMenuUser | null
  /** Login URL for unauthenticated users. If omitted, built from authBase + loginPath + current URL. */
  loginUrl?: string | null
  /** Optional caption rendered under the user's name (e.g. "4 tags"). */
  subtitle?: string
  /** Path on the auth service for account settings. Default: "/account". */
  accountPath?: string
  /** Path on the auth service for logout. Default: "/auth/logout". */
  logoutPath?: string
  /** Path on the auth service for login. Default: "/auth/login". */
  loginPath?: string
  /**
   * Where to send the user after logout. Default: current window.location.origin.
   * Set to override for staging or to send to a marketing page.
   */
  logoutNextUrl?: string
}

/**
 * Top-right account widget. Unauthenticated → "Sign in" button. Signed in →
 * name + dropdown linking to the central auth service's account/logout pages.
 *
 * The account page is always external because the auth service owns it; this
 * widget just provides the entry point inside each downstream app.
 */
export function AccountMenu({
  authBase, user, loginUrl, subtitle,
  accountPath = "/account",
  logoutPath = "/auth/logout",
  loginPath = "/auth/login",
  logoutNextUrl,
}: AccountMenuProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const base = authBase.replace(/\/$/, "")
  const accountUrl = `${base}${accountPath}`
  const nextForLogout = logoutNextUrl ?? (typeof window !== "undefined" ? window.location.origin : "/")
  const logoutUrl = `${base}${logoutPath}?next=${encodeURIComponent(nextForLogout)}`

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  if (!user) {
    const builtLoginUrl =
      loginUrl ??
      `${base}${loginPath}?next=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "/")}`
    return (
      <a href={builtLoginUrl} className="btn-primary">
        <LogIn size={14} /> Sign in
      </a>
    )
  }

  const label = user.name || user.email
  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg border border-th-glass/[0.08] bg-th-glass/[0.04] px-2.5 py-1.5 text-sm text-th-fg/85 transition-colors hover:bg-th-glass/[0.08] hover:text-th-fg"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <div className="text-right leading-tight">
          <div className="text-th-fg">{label}</div>
          {subtitle && <div className="text-[10px] text-th-fg/40">{subtitle}</div>}
        </div>
        <ChevronDown size={13} className="text-th-fg/40" />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-40 mt-1.5 w-56 overflow-hidden rounded-xl border border-th-glass/[0.12] bg-th-bg shadow-xl"
        >
          <div className="border-b border-th-glass/[0.08] px-3 py-2 text-xs text-th-fg/50">
            {user.email}
          </div>
          <a
            href={accountUrl}
            role="menuitem"
            className="flex items-center gap-2 px-3 py-2 text-sm text-th-fg/85 transition-colors hover:bg-th-glass/[0.06] hover:text-th-fg"
          >
            <Settings size={14} /> Account settings
          </a>
          <a
            href={logoutUrl}
            role="menuitem"
            className="flex items-center gap-2 px-3 py-2 text-sm text-red-400 transition-colors hover:bg-red-500/10"
          >
            <LogOut size={14} /> Sign out
          </a>
        </div>
      )}
    </div>
  )
}
